import { memoize } from 'underscore';
import { ReadyState } from 'components/VideoPlayer/utils';
import { IDimensions } from 'types';
import { ApplicationError } from './ApplicationError';
import { UPLOAD_VIDEO_MAX_DURATION_SECONDS } from './constants';
import { isSafari } from './device';
import { secToHours } from './time';

function getSource(src: File | string) {
  return src instanceof File ? URL.createObjectURL(src) : src;
}

type CreateVideoElementOpts = {
  crossOrigin?: HTMLMediaElement['crossOrigin'];
  preload?: HTMLMediaElement['preload'];
};

export function createVideoElement(
  src: File | string,
  opts?: CreateVideoElementOpts,
) {
  if (src === undefined) {
    return undefined;
  }

  const { crossOrigin, preload } = opts ?? {};

  const video = document.createElement('video');
  video.volume = 0;

  if (crossOrigin !== undefined) {
    video.crossOrigin = opts.crossOrigin;
  }

  if (preload !== undefined) {
    video.preload = preload;
  }

  return new Promise<HTMLVideoElement>((resolve, reject) => {
    const removeListeners = () => {
      video.removeEventListener('durationchange', handleDurationChange);
      video.removeEventListener('error', handleError);
    };

    const handleDurationChange = () => {
      resolve(video);
      removeListeners();
    };

    const handleError = () => {
      reject(video.error);
      removeListeners();
    };

    const loadSource = () => {
      video.src = getSource(src);
      video.load();
    };

    video.addEventListener('error', handleError);

    // HACK
    // https://stackoverflow.com/questions/34255345/how-to-get-audio-file-duration-in-safari
    // https://sparemin.atlassian.net/browse/SPAR-8641
    if (isSafari) {
      loadSource();
      video
        .play()
        .then(() => {
          video.pause();
          resolve(video);
        })
        .catch(err => reject(err));
    } else {
      video.addEventListener('durationchange', handleDurationChange);
      loadSource();
    }
  });
}

/**
 * Retrieves the duration of a video in seconds.
 */
export function getDuration(
  src: File | string,
  cb?: (duration: number, err?: Error) => void,
) {
  const result = createVideoElement(src).then(video => video.duration);

  // if the caller passed a cb, call it and return nothing, otherwise return the promise
  if (!cb || typeof cb !== 'function') {
    return result;
  }

  result.then(duration => cb(duration)).catch(err => cb(undefined, err));
  return undefined;
}

export async function verifyVideo(
  file: File,
  maxDurationSeconds = UPLOAD_VIDEO_MAX_DURATION_SECONDS,
) {
  const video = await createVideoElement(file);

  const maxDurationHour = secToHours(maxDurationSeconds);

  if (video.duration > maxDurationSeconds) {
    throw new ApplicationError(
      `Uploaded videos have to be a maximum of ${maxDurationHour} ${
        maxDurationHour === 1 ? 'hour' : 'hours'
      } in length`,
      'IN005',
    );
  }

  /*
   * HACK: not all browsers support all video encodings.  if a video with an unsupported encoding is
   * loaded into the browser, the browser typically just plays the audio without showing any video.
   * codec information isn't exposed in the browser, so assume that if the video is done loading
   * and both videoHeight and videoWidth are 0 the encoding is unsupported
   */
  if (video.videoHeight === 0 || video.videoWidth === 0) {
    throw new ApplicationError(
      "Your browser doesn't support the video's encoding.  Try uploading this video in another browser.",
      'ER008',
    );
  }

  return true;
}

type VideoType = 'ogg' | 'h264' | 'webm' | 'vp9' | 'hls' | string;

/**
 * https://davidwalsh.name/detect-supported-video-formats-javascript
 * https://github.com/Modernizr/Modernizr/blob/5eea7e2a213edc9e83a47b6414d0250468d83471/feature-detects/video.js
 */
export const supportsVideoType = memoize((type: VideoType) => {
  let video: HTMLVideoElement;

  // Allow user to create shortcuts, i.e. just "webm"
  const formats: Record<VideoType, string> = {
    ogg: 'video/ogg; codecs="theora"',
    h264: 'video/mp4; codecs="avc1.42E01E"',
    webm: 'video/webm; codecs="vp8, vorbis"',
    vp9: 'video/webm; codecs="vp9"',
    hls: 'application/x-mpegURL; codecs="avc1.42E01E"',
  };

  if (!video) {
    video = document.createElement('video');
  }

  try {
    return !!video.canPlayType(formats[type] || type).replace(/^no$/, '');
  } catch {
    return false;
  }
});

export function supportsWebM() {
  return supportsVideoType('webm') && supportsVideoType('vp9');
}

export function getVideoSize(url: string): Promise<IDimensions> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');

    video.src = url;

    const handleMetadataLoaded = () => {
      resolve({
        width: video.videoWidth,
        height: video.videoHeight,
      });
    };

    video.addEventListener('loadedmetadata', handleMetadataLoaded);

    video.addEventListener('error', () => {
      reject(video.error);
      video.removeEventListener('loadedmetadata', handleMetadataLoaded);
    });
  });
}

async function waitForMetadata(video: HTMLVideoElement) {
  return new Promise<void>((resolve, reject) => {
    if (video.readyState >= ReadyState.HAVE_METADATA) {
      resolve();
    } else {
      const removeHandlers = () => {
        video.removeEventListener('loadedmetadata', handleMeta);
        video.removeEventListener('error', handleError);
      };
      const handleError = () => {
        removeHandlers();
        reject(video.error);
      };
      const handleMeta = () => {
        removeHandlers();
        resolve();
      };

      video.addEventListener('error', handleError);
      video.addEventListener('loadedmetadata', handleMeta);
    }
  });
}

export async function waitForSeeked(video: HTMLVideoElement): Promise<void> {
  return new Promise<void>(resolve => {
    const handleSeeked = () => {
      video.removeEventListener('seeked', handleSeeked);
      resolve();
    };
    video.addEventListener('seeked', handleSeeked);
  });
}

async function seekToTime(video: HTMLVideoElement, time: number) {
  return new Promise<void>((resolve, reject) => {
    const removeHandlers = () => {
      video.removeEventListener('seeked', handleSeeked);
      video.removeEventListener('error', handleError);
    };
    const handleSeeked = () => {
      removeHandlers();
      resolve();
    };
    const handleError = () => {
      removeHandlers();
      reject(video.error);
    };

    video.addEventListener('error', handleError);
    video.addEventListener('seeked', handleSeeked);

    video.currentTime = time;
  });
}

function forceVideoRerender(video: HTMLVideoElement): void {
  video.currentTime += 1;
  video.currentTime -= 1;
}

export function getMediaTime(video: HTMLVideoElement): Promise<number> {
  return new Promise<number>(resolve => {
    video.requestVideoFrameCallback((_, meta) => {
      resolve(meta.mediaTime ?? video.currentTime);
    });
  });
}

/**
 * Frame-accurate seek utility for any HTMLVideoElement
 *
 * Uses requestVideoFrameCallback to step frame-by-frame until reaching target.
 *
 * @param video - the HTMLVideoElement to seek
 * @param targetTimeSec  - Target time in seconds (in videos' native time)
 * @returns Promise resolving to the actual frame time achieved
 */
export async function seekToFrameAccurate(
  video: HTMLVideoElement,
  targetTimeSec: number,
): Promise<number> {
  // seek to approximate position (slightly before target)
  const seekBeforeSec = Math.max(0, targetTimeSec - 0.1);
  video.currentTime = seekBeforeSec;

  // wait for initial seek to complete
  await waitForSeeked(video);

  // get starting frame time
  forceVideoRerender(video);
  let currentFrameTime = await getMediaTime(video);

  // step forward, frame-by-frame, until we reach/pass the target
  while (currentFrameTime < targetTimeSec) {
    const frameTimeBeforeStep = currentFrameTime;

    // step forward one frame
    for (;;) {
      video.currentTime += 0.01;
      forceVideoRerender(video);
      const newFrameTime = await getMediaTime(video);

      // if frame time changed, we've advanced one frame
      if (newFrameTime !== frameTimeBeforeStep) {
        currentFrameTime = newFrameTime;
        break;
      }

      // safety: if we've incremented a lot and still no change, we're stuck
      if (video.currentTime > targetTimeSec + 1) {
        break;
      }
    }

    // if we didn't advance, we're stuck
    if (currentFrameTime === frameTimeBeforeStep) {
      break;
    }
  }

  return currentFrameTime;
}

function exportCurrentVideoFrame(video: HTMLVideoElement, scale: number = 1) {
  // Extract dimensions for the thumbnail.
  const originalWidth = video.videoWidth;
  const originalHeight = video.videoHeight;

  // Create a canvas for rendering the thumbnail.
  const canvas = document.createElement('canvas');

  canvas.width = Math.round(originalWidth * scale);
  canvas.height = Math.round(originalHeight * scale);

  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Failed to get 2D context for canvas.');
  }

  // Draw the video frame onto the canvas.
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

  // Convert canvas to a PNG data URL.
  return canvas.toDataURL('image/png');
}

type CreateVideoThumbnailOpts = {
  scale?: number;
  time?: number;
};

/**
 * Generates a thumbnail from the first frame of a video file or URL.
 *
 * if input is a video, it might need crossOrigin set to 'anonymous'
 */
export async function getVideoThumbnail(
  input: HTMLVideoElement | File | string,
  { time = 0, scale = 1 }: CreateVideoThumbnailOpts = {},
) {
  const video =
    input instanceof HTMLVideoElement
      ? input
      : await createVideoElement(input, {
          crossOrigin: 'anonymous',
          preload: 'metadata',
        });

  await waitForMetadata(video);
  await seekToTime(video, time);
  return exportCurrentVideoFrame(video, scale);
}
